import React, { useState, useEffect } from 'react'
import { useDisableBodyScroll } from 'hooks/useDisableBodyScroll'
import { MediaRenderer } from 'components/index.js'
import { Dialog, DialogContent } from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { Download, Copy, X, ZoomIn, ZoomOut, Maximize } from 'lucide-react'
import { ScrollArea } from '@components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip'
import { copyToClipboard } from 'utils/mediaUtils'
import { useDispatch } from 'react-redux'

const MediaViewModal = ({ onClose, mediaUrl, setinlargeData, isWatermark, assets, canDownload, onDownload }) => {
  useDisableBodyScroll()
  const dispatch = useDispatch()

  const [zoom, setZoom] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)

  const copySuccess = false

  // Reset zoom and position when image changes
  useEffect(() => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setIsFullscreen(false)
  }, [])

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5))
    if (zoom <= 1) {
      setPosition({ x: 0, y: 0 })
    }
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true)
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Get the current image URL based on whether we're viewing   mediaUrl
  const currentImageUrl = mediaUrl

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        className={`max-w-[95vw] max-h-[95vh] w-[60vmax] h-[60vmax] p-0 overflow-hidden rounded-lg bg-gray-900/90 backdrop-blur-md ${isFullscreen ? 'max-w-[98vw] max-h-[98vh] w-[98vw]' : ''}`}
        onInteractOutside={(e) => e.preventDefault()}
        isCloseVisible={false}
      >
        <div className="absolute right-2 top-2 z-50 flex gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-black/40 hover:bg-black/60 text-white rounded-full"
                  onClick={toggleFullscreen}
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {zoom > 0.5 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/40 hover:bg-black/60 text-white rounded-full"
                    onClick={handleZoomOut}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Zoom out</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {zoom < 3 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/40 hover:bg-black/60 text-white rounded-full"
                    onClick={handleZoomIn}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Zoom in</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canDownload && currentImageUrl && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/40 hover:bg-black/60 text-white rounded-full"
                    onClick={() => onDownload(currentImageUrl)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {currentImageUrl && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`bg-black/40 hover:bg-black/60 text-white rounded-full ${copySuccess ? 'bg-green-500/40' : ''}`}
                    onClick={() => dispatch(copyToClipboard(mediaUrl))}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{copySuccess ? 'Copied!' : 'Copy link'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <Button
            onClick={() => onClose()}
            variant="ghost"
            size="icon"
            className="bg-black/40 hover:bg-black/60 text-white rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="h-full flex flex-col">
          <div
            className={`flex-1 flex items-center justify-center p-4 ${Array.isArray(assets) && assets?.length > 1 && !isFullscreen ? 'pb-4' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
          >
            <div
              className="relative transition-transform w-[50vmax] h-[50vmax] overflow-hidden shadow-2xl rounded-lg flex items-center justify-center"
              style={{
                transform:
                  zoom > 1
                    ? `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`
                    : `scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              }}
            >
              <MediaRenderer
                item={mediaUrl}
                mediaKey={`lg-media`}
                classNames="w-full h-full relative flex items-center justify-center"
                imageClassNames="w-auto h-auto max-w-full max-h-full object-contain"
                image3dClassNames="w-full h-[700px]"
                videoClassNames="w-auto h-auto max-w-full max-h-full"
                textClassNames="w-full h-full"
                copyOnHover={false}
                enableCopy={false}
                isWatermark={isWatermark}
              />
            </div>
          </div>

          {Array.isArray(assets) && assets?.length > 1 && !isFullscreen && (
            <div className="p-4 bg-gray-800/60 border-t border-gray-700">
              <ScrollArea className="w-full" orientation="horizontal">
                <div className="flex gap-3 pb-2">
                  {assets.map((item, i) => (
                    <Button
                      key={`sm-media-${i}`}
                      variant="ghost"
                      className={`p-0 h-24 w-24 rounded-md overflow-hidden border-2 shadow-md transition-all transform hover:scale-105 ${
                        mediaUrl === item ? 'border-primary ring-2 ring-primary/50' : 'border-transparent'
                      }`}
                      onClick={() => {
                        setinlargeData({
                          img: item,
                          multipleImg: assets,
                        })
                      }}
                    >
                      <MediaRenderer
                        item={item}
                        mediaKey={`sm-media-${i}`}
                        classNames="h-full w-full"
                        imageClassNames="h-full w-full object-cover"
                        videoClassNames="h-full w-full object-cover"
                        textClassNames="h-full w-full"
                        image3dClassNames="h-full w-full"
                        isWatermark={isWatermark}
                        enableCopy={false}
                      />
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MediaViewModal
